import React, { useState, useEffect, useCallback } from 'react';
import { agenciesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiRefreshCw, FiAlertTriangle, FiGlobe, FiSlash, FiCheckCircle, FiX } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const TYPE_LABELS = {
  IN_HOUSE: 'In-House',
  SUB_AGENT: 'Sub-Agent',
  INDIVIDUAL_AGENT: 'Individual Agent',
  PARTNER_SCHOOL: 'Partner School',
  HOSPITAL: 'Hospital',
};

const SuspendModal = ({ agency, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 18 }}>Suspend "{agency.name}"?</h2>
        </div>
        <div className="modal-form">
          <p style={{ margin: 0, color: '#666', fontSize: 13, lineHeight: 1.5 }}>
            This immediately blocks login and API access for all {agency.member_count} member(s) of this agency.
            Historical data is kept — nothing is deleted.
          </p>
          <div className="form-group">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional, e.g. fraud complaint, quality issue)"
              rows={3}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => onConfirm(reason)}
            disabled={loading}
            style={{ background: '#dc2626', borderColor: '#dc2626' }}
          >
            {loading ? 'Suspending…' : 'Yes, Suspend'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Agencies = () => {
  const { canSuspendAgencies, isCsm } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await agenciesAPI.list();
      setAgencies(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load agencies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSuspend = async (reason) => {
    setActionLoading(true);
    try {
      await agenciesAPI.suspend(suspendTarget.id, reason);
      setSuspendTarget(null);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to suspend agency.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstate = async (agency) => {
    setActionLoading(true);
    try {
      await agenciesAPI.reinstate(agency.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reinstate agency.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiGlobe size={22} color="#011ff3" />
            Agencies &amp; Countries
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            In-house teams, sub-agents, and partner schools. <strong style={{ color: '#64748b' }}>{agencies.length}</strong> total.
            {!canSuspendAgencies() && (
              <span style={{ color: '#f5a623', marginLeft: 8 }}>
                {isCsm() ? '· Read-only view' : '· Only Chairman/Admin can suspend or reinstate'}
              </span>
            )}
          </p>
        </div>
        <button className="btn-icon" onClick={load} title="Refresh">
          <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertTriangle size={16} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={14} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : agencies.length === 0 ? (
          <div className="empty-state">No agencies yet.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th><th>Type</th><th>Country</th><th>Members</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agencies.map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.name}</strong></td>
                  <td>{TYPE_LABELS[a.agency_type] || a.agency_type}</td>
                  <td>{a.country || '—'}</td>
                  <td>{a.member_count}</td>
                  <td>
                    {a.is_suspended ? (
                      <span title={a.suspension_reason} className="status-pill" style={{ color: '#f5576c' }}>Suspended</span>
                    ) : (
                      <span className="status-pill" style={{ color: '#43e97b' }}>Active</span>
                    )}
                  </td>
                  <td>
                    {canSuspendAgencies() && (
                      a.is_suspended ? (
                        <button className="cv-inline-btn" onClick={() => handleReinstate(a)} disabled={actionLoading} style={{ color: '#43e97b' }}>
                          <FiCheckCircle size={13} /> Reinstate
                        </button>
                      ) : (
                        <button className="cv-inline-btn" onClick={() => setSuspendTarget(a)} disabled={actionLoading} style={{ color: '#f5576c' }}>
                          <FiSlash size={13} /> Suspend
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {suspendTarget && (
        <SuspendModal
          agency={suspendTarget}
          onConfirm={handleSuspend}
          onCancel={() => setSuspendTarget(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default Agencies;
